<template>
    <div class="notification-wrapper">
        <!-- Guest login button -->
        <button v-if="!isAuthenticated" class="login-button" @click="$emit('login-click')">
            Войти
        </button>

        <!-- Authenticated notifications menu -->
        <v-menu
            v-else
            v-model="open"
            :close-on-content-click="false"
            location="bottom end"
            offset="8"
            transition="fade-transition"
            max-width="min(440px, calc(100vw - 32px))"
        >
            <!-- Кнопка-колокол -->
            <template #activator="{ props: activator }">
                <v-btn
                    icon
                    variant="text"
                    class="notification-trigger"
                    v-bind="activator"
                    :aria-label="`Уведомления (${notifications.unreadCount})`"
                >
                    <div class="bell-icon-container">
                        <svg width="22" height="26" viewBox="0 0 22 26" fill="none" xmlns="http://www.w3.org/2000/svg"
                            class="bell-icon">
                            <path
                                d="M14.6666 19.1111H20.7777L19.0606 17.394C18.5949 16.9283 18.3333 16.2967 18.3333 15.6381V11.7778C18.3333 8.5848 16.2926 5.86844 13.4444 4.86173V4.44444C13.4444 3.09442 12.35 2 10.9999 2C9.64992 2 8.5555 3.09441 8.5555 4.44444V4.86173C5.70725 5.86844 3.66661 8.5848 3.66661 11.7778V15.6381C3.66661 16.2967 3.40499 16.9283 2.93931 17.394L1.22217 19.1111H7.33328M14.6666 19.1111V20.3333C14.6666 22.3584 13.025 24 10.9999 24C8.9749 24 7.33328 22.3584 7.33328 20.3333V19.1111M14.6666 19.1111H7.33328"
                                stroke="currentColor" stroke-width="2.04545" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                        <v-badge
                            v-if="notifications.unreadCount"
                            :content="notifications.unreadCount > 99 ? '99+' : notifications.unreadCount"
                            color="error"
                            class="notification-badge"
                        />
                    </div>
                </v-btn>
            </template>

            <!-- Выпадающее меню -->
            <v-card class="notification-dropdown" elevation="8" :rounded="12">
                <!-- Заголовок -->
                <div class="dropdown-header">
                    <h3 class="header-title">Уведомления</h3>
                    <v-chip
                        v-if="notifications.unreadCount"
                        size="small"
                        color="primary"
                        variant="flat"
                        class="unread-chip"
                    >
                        {{ notifications.unreadCount }} новых
                    </v-chip>
                </div>

                <v-divider class="header-divider" />

                <!-- Единый хронологический список (created_at DESC).
                     LG-025: обёртка нужна для градиент-подсказки у нижнего
                     края — без неё третья карточка обрезалась ровно посередине
                     строки текста, и было не видно, что список продолжается
                     (полоса прокрутки появляется только при наведении). -->
                <div class="notifications-scroll">
                <div ref="scrollEl" class="notifications-container" @scroll="updateScrollHint">
                    <div v-if="dropdownItems.length" class="notif-list">
                        <NotificationCard
                            v-for="n in dropdownItems"
                            :key="n.public_id"
                            :notification="n"
                            @act="onAct"
                            @dismiss="onDismiss"
                            @open="onOpenItem"
                        />
                    </div>

                    <!-- Пустое состояние -->
                    <v-empty-state
                        v-else
                        icon="mdi-bell-check-outline"
                        title="Пока тихо"
                        text="Здесь появятся новые уведомления."
                        class="notif-empty"
                    />
                </div>
                <!-- Не перехватывает нажатия: под градиентом карточка
                     по-прежнему кликается. -->
                <div
                    v-if="hasMoreToScroll"
                    class="notifications-scroll__hint"
                    data-testid="notifications-scroll-hint"
                    aria-hidden="true"
                />
                </div>

                <v-divider class="header-divider" />

                <!-- Действия -->
                <div class="dropdown-actions">
                    <v-btn
                        variant="text"
                        size="small"
                        color="primary"
                        class="action-button"
                        @click="onOpenAll"
                    >
                        Все уведомления
                    </v-btn>
                </div>
            </v-card>
        </v-menu>
    </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useNotifications } from '../../../store/notifications';
import NotificationCard from '@/components/notifications/NotificationCard.vue';
import { useNotificationsRealtime } from '@/composables/useNotificationsRealtime';
import type { RawNotification } from '@/shared/types/notification';

const rootStore = useStore();
const router = useRouter();
const notifications = useNotifications();
const realtime = useNotificationsRealtime(notifications);

defineEmits<{
    (e: 'login-click'): void;
}>();

const isAuthenticated = computed(
    () => rootStore.getters.isAuthenticated,
);

const open = ref(false);

const POLL_INTERVAL_MS = 30_000;
// L1 — когда WS не встаёт (realtime.degraded), polling становится единственным
// путём доставки, поэтому ужимаем интервал, ограничивая worst-case свежесть
// колокола (~10 c вместо 30 c). При восстановлении WS возвращаемся к 30 c.
const DEGRADED_POLL_INTERVAL_MS = 10_000;
const JITTER_MS = 5_000;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
// E04 — гигиена: привязываем HTTP-запросы поллинга к жизненному циклу
// подписок (startSubscriptions/stopSubscriptions), чтобы при unmount/логауте
// in-flight refreshUnreadCount/refreshFromServer отменялись, а не «висели».
let pollController: AbortController | null = null;

// UX 5.2/5.4: один хронологический список вместо деления action/info.
// Стор держит `items` уже отсортированными created_at DESC (инвариант 5.4),
// поэтому здесь НЕ сортируем — просто берём префикс для дропдауна.
// dismiss физически убирает item из стора (5.5), а viewed/acted остаются
// в ленте как «прочитанные».
const DROPDOWN_LIMIT = 8;
const dropdownItems = computed<RawNotification[]>(() =>
    // LG-023: feedItems — та же лента, но по одному обращению одна карточка.
    notifications.feedItems.slice(0, DROPDOWN_LIMIT),
);

// ── LG-025: подсказка о прокрутке ────────────────────────────────────────
//
// Третья карточка обрезалась нижним краем панели ровно посередине строки, и
// понять, что список продолжается, было нельзя: полоса прокрутки в этом
// браузерном стиле появляется только при наведении.
const scrollEl = ref<HTMLElement | null>(null);
const hasMoreToScroll = ref(false);

function updateScrollHint(): void {
    const el = scrollEl.value;
    if (!el) {
        hasMoreToScroll.value = false;
        return;
    }
    // Порог в 4px: на дробном devicePixelRatio scrollHeight и clientHeight
    // расходятся на доли пикселя, и градиент мигал бы на конце списка.
    hasMoreToScroll.value = el.scrollHeight - el.scrollTop - el.clientHeight > 4;
}

// Пересчитываем, когда список изменился: пришло новое уведомление, сработал
// dismiss или дропдаун открылся впервые.
watch(
    dropdownItems,
    () => {
        void nextTick(updateScrollHint);
    },
    { immediate: true, deep: false },
);

function schedulePoll() {
    if (typeof document !== 'undefined' && document.hidden) return;
    const base = realtime.degraded.value
        ? DEGRADED_POLL_INTERVAL_MS
        : POLL_INTERVAL_MS;
    const next = base + Math.random() * JITTER_MS;
    pollTimer = setTimeout(async () => {
        pollTimer = null;
        // Когда WS деградировал, polling — единственный путь доставки: тянем
        // count+list (refreshFromServer), чтобы новые карточки появлялись в
        // дропдауне сразу, а не только при ручном открытии (баг #6, ветка
        // fallback). При живом WS хватает дешёвого refreshUnreadCount — список
        // наполняет WS-пуш (pushFromSocket), лишний list-запрос не нужен.
        await pollRefresh();
        schedulePoll();
    }, next);
}

// Свежесть колокола по polling'у: список тянем только на degraded-ветке
// (WS не доставляет), иначе — только счётчик. Держит happy-path дешёвым.
async function pollRefresh(): Promise<void> {
    if (realtime.degraded.value) {
        await notifications.refreshFromServer(pollController?.signal);
    } else {
        await notifications.refreshUnreadCount(pollController?.signal);
    }
}

function clearPoll() {
    if (pollTimer !== null) {
        clearTimeout(pollTimer);
        pollTimer = null;
    }
}

function handleVisibilityChange() {
    if (typeof document === 'undefined') return;
    if (!document.hidden && pollTimer === null) {
        void notifications.refreshUnreadCount(pollController?.signal);
        schedulePoll();
    } else if (document.hidden) {
        clearPoll();
    }
}

function handleWindowFocus() {
    void notifications.refreshUnreadCount(pollController?.signal);
}

function handleTokenRefresh() {
    void notifications.refreshUnreadCount(pollController?.signal);
}

// Доставка уведомлений в колокол — ДВА независимых пути:
//   1) realtime WS-пуш (useNotificationsRealtime → singleton /ws/notifications/):
//      бэк бродкастит каждое платформенное уведомление, и оно появляется в
//      колоколе БЕЗ перезагрузки страницы (баги КУ #29/#30/#99). Сокет — один
//      на весь app-session (см. useNotificationSocket), поэтому прежнее
//      возражение «по коннекту на КАЖДОЙ странице, слишком жирно» снято:
//      ChatsPage и колокол делят один сокет.
//   2) polling раз в 30 сек + jitter (schedulePoll) — safety-net на случай,
//      если WS оборвался / прокся гасит upgrade / cookie протух.
//
// Гейтим оба пути на isAuthenticated, чтобы гости не открывали сокет и не
// ловили 401-шум на /unread_count/.
function startSubscriptions() {
    realtime.start();
    if (pollTimer !== null) return; // polling уже идёт
    pollController = typeof AbortController !== 'undefined' ? new AbortController() : null;
    void notifications.refreshUnreadCount(pollController?.signal);
    schedulePoll();
}

function stopSubscriptions() {
    realtime.stop();
    clearPoll();
    // E04 — гасим ещё не завершившийся poll-запрос (unmount/логаут), чтобы
    // ответ не прилетел в стор уже неактуальным.
    pollController?.abort();
    pollController = null;
}

onMounted(() => {
    if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', handleVisibilityChange);
    }
    if (typeof window !== 'undefined') {
        window.addEventListener('focus', handleWindowFocus);
        window.addEventListener('jwt-refreshed', handleTokenRefresh);
    }
});

watch(
    isAuthenticated,
    (authed) => {
        if (authed) {
            startSubscriptions();
        } else {
            stopSubscriptions();
        }
    },
    { immediate: true },
);

// L1 — при деградации WS немедленно переходим на учащённый polling (а не ждём
// конца текущего 30-секундного таймера); при восстановлении следующий цикл сам
// возьмёт расслабленный интервал.
watch(
    () => realtime.degraded.value,
    (isDegraded) => {
        if (!isAuthenticated.value) return;
        if (isDegraded && pollTimer !== null) {
            clearPoll();
            // Немедленный catch-up на degraded-ветке — count+list (см. pollRefresh).
            void pollRefresh();
            schedulePoll();
        }
    },
);

onBeforeUnmount(() => {
    stopSubscriptions();
    if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
    if (typeof window !== 'undefined') {
        window.removeEventListener('focus', handleWindowFocus);
        window.removeEventListener('jwt-refreshed', handleTokenRefresh);
    }
});

watch(open, async (isOpen) => {
    if (!isOpen) return;
    if (!notifications.loaded) await notifications.load();
    const pendingIds = notifications.items
        .filter(n => n.status === 'pending')
        .map(n => n.public_id)
        .filter((id): id is string => !!id);
    if (pendingIds.length) await notifications.markViewed(pendingIds);
});

async function onAct(publicId: string, href: string) {
    await notifications.act(publicId);
    open.value = false;
    if (href) router.push(href);
}

async function onDismiss(publicId: string) {
    await notifications.dismissGroup(publicId);
}

function onOpenItem(publicId: string) {
    open.value = false;
    router.push({ path: '/notifications', query: { highlight: publicId } });
}

function onOpenAll() {
    open.value = false;
    router.push('/notifications');
}
</script>

<style scoped>
.notification-wrapper {
    display: flex;
    align-items: center;
}

.login-button {
    padding: 8px 20px;
    font-size: 14px;
    font-weight: 500;
    color: rgb(123, 101, 247);
    background: white;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
}

.login-button:hover {
    background: rgba(123, 101, 247, 0.08);
    transform: translateY(-1px);
}

.login-button:active {
    transform: translateY(0);
}

.notification-trigger {
    width: 40px !important;
    height: 40px !important;
    color: rgb(82, 90, 125);
    background-color: white !important;
    border-radius: 50% !important;
    transition: all 0.3s ease;
    overflow: visible !important;
}

.notification-trigger:hover {
    background-color: rgba(123, 101, 247, 0.08) !important;
    color: rgb(123, 101, 247);
}

.bell-icon-container {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
}

.bell-icon {
    transition: transform 0.3s ease;
}

.notification-trigger:hover .bell-icon {
    transform: rotate(15deg);
}

.notification-badge {
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 22px;
    height: 22px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 10px rgba(220, 38, 38, 0.35);
    padding: 3px 6px;
}

:deep(.notification-badge .v-badge__badge) {
    bottom: calc(100% - 10px) !important;
    left: calc(100% - 10px) !important;
}

.notification-dropdown {
    width: min(440px, calc(100vw - 32px));
    max-height: min(600px, calc(100vh - 100px));
    border-radius: 12px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.v-overlay__content .notification-dropdown {
    border-radius: 12px !important;
}

.dropdown-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 20px 16px;
    gap: 12px;
}

.header-title {
    font-size: 18px;
    font-weight: 600;
    color: rgb(33, 33, 33);
    margin: 0;
    line-height: 1.4;
}

.unread-chip {
    font-size: 12px;
    font-weight: 600;
    height: 24px;
    min-width: 24px;
}

.header-divider {
    margin: 0 !important;
}

.notifications-scroll {
    position: relative;
    flex: 1;
    min-height: 0;
    display: flex;
}

.notifications-container {
    flex: 1;
    overflow-y: auto;
    min-height: 200px;
    max-height: calc(100vh - 360px);
    padding: 12px;
}

/* LG-025: подсказка «список продолжается». Появляется только когда есть куда
   прокручивать, и гаснет на конце списка — см. updateScrollHint. */
.notifications-scroll__hint {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 36px;
    pointer-events: none;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #ffffff 92%);
}

.notif-list > * + * {
    margin-top: 8px;
}

.notif-empty {
    padding: 32px 16px;
}

.dropdown-actions {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    padding: 12px;
    flex-wrap: wrap;
}

.action-button {
    font-size: 13px;
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
}

.notifications-container::-webkit-scrollbar {
    width: 6px;
}

.notifications-container::-webkit-scrollbar-track {
    background: transparent;
}

.notifications-container::-webkit-scrollbar-thumb {
    background: rgba(123, 101, 247, 0.2);
    border-radius: 3px;
}

.notifications-container::-webkit-scrollbar-thumb:hover {
    background: rgba(123, 101, 247, 0.3);
}

@media (max-width: 600px) {
    .notification-dropdown {
        width: calc(100vw - 16px);
        max-height: calc(100vh - 80px);
    }

    .dropdown-header {
        padding: 16px 16px 12px;
    }

    .header-title {
        font-size: 16px;
    }

    .dropdown-actions {
        padding: 8px;
        gap: 4px;
    }

    .action-button {
        font-size: 12px;
        padding: 4px 8px !important;
    }

    .notifications-container {
        padding: 8px;
    }
}

@media (max-width: 400px) {
    .dropdown-actions {
        flex-direction: column;
        align-items: stretch;
    }

    .action-button {
        width: 100%;
        justify-content: center;
    }
}
</style>
